import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock, FileWarning, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth";

import UploadZone from "@/components/UploadZone";
import ReportTable from "@/components/ReportTable";
import ReportDetailModal from "@/components/ReportDetailModal";
import FailedReportsModal from "@/components/FailedReportsModal";

import BatchList from "@/components/dashboard/BatchList";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import StatsSection from "@/components/dashboard/StatsSection";
import ReportFilters from "@/components/dashboard/ReportFilters";
import HowItWorksCard from "@/components/dashboard/HowItWorksCard";
import UploadReconciliation from "@/components/dashboard/UploadReconciliation";

import { useJobPolling } from "@/hooks/useJobPolling";
import { createJob, deleteJob, getJob, reEnrichJob, resetBudget, resumeJob, retryFailedJob, uploadFilesChunked } from "@/lib/api";

const JOB_KEY = "crash_job_id";

export default function Dashboard() {
  const { logout } = useAuth();

  const [injuryFilter, setInjuryFilter] = useState("");
  const [search, setSearch] = useState("");

  const {
    jobId,
    setJobId,
    clearLocal,
    reports,
    loadingReports,
    budget,
    stats,
    refreshJob,
    refreshReports,
    refreshBudget,
    POLL_INTERVAL,
    job,
    queueStatus,
  } = useJobPolling({ injuryFilter, search });

  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadDetail, setUploadDetail] = useState("");
  const [lastUpload, setLastUpload] = useState(null);

  const [openReport, setOpenReport] = useState(null);
  const [failedOpen, setFailedOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [reEnriching, setReEnriching] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const onProgress = (pct, info) => {
    setUploadPct(pct);
    if (info?.file) {
      setUploadDetail(
        `${info.file} · part ${info.chunk}/${info.totalChunks} · file ${info.fileIndex}/${info.fileCount}`
      );
    }
  };

  const verifyOrCreateJob = async () => {
    if (jobId) {
      try {
        await getJob(jobId);
        return jobId;
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
        clearLocal();
      }
    }
    const j = await createJob();
    setJobId(j.id);
    return j.id;
  };

  const handleFiles = async (files) => {
    setUploading(true);
    setUploadPct(0);
    setUploadDetail("");
    try {
      const id = await verifyOrCreateJob();
      let res;
      try {
        res = await uploadFilesChunked(id, files, onProgress);
      } catch (err) {
        // If the server says the job vanished mid-upload, recreate and retry once.
        if (err?.response?.status !== 404) throw err;
        clearLocal();
        const j = await createJob();
        setJobId(j.id);
        res = await uploadFilesChunked(j.id, files, onProgress);
      }
      const n = res.accepted_files ?? res.queued ?? files.length;
      const skipped = Array.isArray(res.skipped) ? res.skipped : [];
      setLastUpload({
        uploadedCount: files.length,
        acceptedFiles: n,
        skipped,
        at: Date.now(),
      });
      if (skipped.length > 0) {
        // Critical: surface lost files immediately. The user has historically
        // seen "N missing, no red X" on production because the upload pipeline
        // silently dropped files. We now ALWAYS show this.
        const first = skipped.slice(0, 3).map((s) => s.file_name || "unknown").join(", ");
        const rest = skipped.length > 3 ? ` and ${skipped.length - 3} more` : "";
        toast.error(
          `${skipped.length} file${skipped.length === 1 ? "" : "s"} could not be assembled (${first}${rest}). Please re-upload those.`,
          { duration: 12000 }
        );
      }
      if (n < files.length) {
        toast.warning(
          `Uploaded ${files.length} but only ${n} accepted by the server. ${files.length - n} did not make it — please re-upload.`,
          { duration: 12000 }
        );
      } else {
        toast.success(`Uploaded ${n} file${n === 1 ? "" : "s"} — extraction running in background`);
      }
      const isBatch = files.length > 3 || files.some((f) => f.name.toLowerCase().endsWith(".zip"));
      if (isBatch) {
        toast.message(
          "Heads-up: each report uses AI credits (~$0.01–0.05). Enable auto top-up (Profile → Universal Key) so big batches never stall mid-run.",
          { duration: 9000 }
        );
      }
      refreshJob();
      refreshReports();
    } catch (e) {
      toast.error("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      setUploadPct(0);
      setUploadDetail("");
    }
  };

  const handleClear = async () => {
    if (!jobId) return;
    setClearing(true);
    try {
      await deleteJob(jobId);
      localStorage.removeItem(JOB_KEY);
      clearLocal();
      setLastUpload(null);
      toast.success("Cleared all reports");
    } catch (e) {
      toast.error("Clear failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setClearing(false);
      setConfirmClearOpen(false);
    }
  };

  const handleRefresh = async () => {
    if (!jobId) {
      toast.message("Nothing to refresh — upload some files to start a job.");
      return;
    }
    setRefreshing(true);
    try {
      await Promise.all([refreshJob(), refreshReports(), refreshBudget()]);
      toast.success("Refreshed");
    } catch (e) {
      toast.error("Refresh failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRefreshing(false);
    }
  };

  const handleResumeStuck = async () => {
    if (!jobId) return;
    try {
      const res = await resumeJob(jobId);
      if (res.stuck === 0) {
        toast.message("Nothing stuck — already processing.");
      } else {
        toast.success(`Re-queued ${res.resumed} of ${res.stuck} stuck file${res.stuck === 1 ? "" : "s"}`);
      }
      refreshJob();
      refreshReports();
    } catch (e) {
      toast.error("Resume failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleRetryFailed = async () => {
    if (!jobId || retrying) return;
    setRetrying(true);
    try {
      const res = await retryFailedJob(jobId);
      const unrec = res.unrecoverable || 0;
      if (res.retried === 0 && unrec > 0) {
        toast.message(
          `${unrec} file${unrec === 1 ? " is" : "s are"} unrecoverable — the PDF is gone from the server. Re-upload the ZIP to replace ${unrec === 1 ? "it" : "them"} (already-processed files won't be duplicated).`,
          { duration: 10000 }
        );
      } else if (res.retried === 0) {
        toast.message("No failed files to retry.");
      } else {
        toast.success(
          `Retrying ${res.retried} failed file${res.retried === 1 ? "" : "s"}…` +
            (unrec > 0 ? ` ${unrec} unrecoverable — re-upload the ZIP for those.` : "")
        );
      }
      refreshJob();
      refreshReports();
    } catch (e) {
      toast.error("Retry failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRetrying(false);
    }
  };

  const reEnrichCount = useMemo(
    () =>
      reports.filter(
        (r) =>
          r.status === "done" &&
          !r.narrative_excerpt &&
          (!r.insurance_info || r.insurance_info.length === 0)
      ).length,
    [reports]
  );

  const handleReEnrich = async () => {
    if (!jobId || reEnriching) return;
    setReEnriching(true);
    try {
      const res = await reEnrichJob(jobId);
      if (res.queued === 0) {
        toast.message("Nothing to re-enrich — all qualifying reports already have narrative & insurance data.");
      } else {
        toast.success(`Re-enriching ${res.queued} report${res.queued === 1 ? "" : "s"} — narrative, fault & insurance will fill in as they finish.`);
      }
      refreshJob();
      refreshReports();
    } catch (e) {
      toast.error("Re-enrich failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setReEnriching(false);
    }
  };

  const handleBudgetReset = async () => {
    try {
      await resetBudget();
      toast.success("Resumed. New uploads will extract again.");
      refreshBudget();
    } catch (e) {
      toast.error("Could not resume: " + (e?.response?.data?.detail || e.message));
    }
  };

  const isQueued = job?.queue_state === "queued" && stats.total > stats.processed;
  const isProcessing = job?.status === "processing" && stats.total > 0 && !isQueued;
  const pendingCount = Math.max(stats.total - stats.processed, 0);
  // Someone ELSE's report is running (not the batch open on this screen).
  const otherReportActive = queueStatus.active && queueStatus.active_job_id !== jobId;

  return (
    <div className="min-h-screen bg-white text-black">
      <DashboardHeader
        jobId={jobId}
        refreshing={refreshing}
        clearing={clearing}
        onRefresh={handleRefresh}
        onClear={() => setConfirmClearOpen(true)}
        onLogout={logout}
      />

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 space-y-8">
        <BatchList
          jobId={jobId}
          onOpen={(id) => {
            setLastUpload(null);
            setJobId(id);
          }}
          onNewBatch={() => {
            setLastUpload(null);
            clearLocal();
            toast.message("Fresh batch — upload files to begin. Previous batches stay in the list.");
          }}
        />

        {/* Upload + How it works */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            {otherReportActive && (
              <div
                data-testid="global-queue-banner"
                className="mb-3 border-2 border-black bg-[#FFE066] text-black px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] flex items-center gap-2 flex-wrap"
              >
                <Clock className="w-4 h-4 shrink-0" />
                A report is already running for another staff member.
                <span className="font-bold">You can still upload</span> — your batch is saved immediately and
                will start automatically when the current one finishes
                {queueStatus.queued_count > 0 ? ` (${queueStatus.queued_count} already in line)` : ""}.
              </div>
            )}
            <UploadZone onFiles={handleFiles} disabled={uploading} />
            {uploading && (
              <div
                data-testid="upload-progress"
                className="mt-3 border border-black bg-white font-mono text-[11px] uppercase tracking-[0.2em]"
              >
                <div className="px-3 py-1.5 flex justify-between">
                  <span>Uploading…</span>
                  <span>{uploadPct}%</span>
                </div>
                {uploadDetail && (
                  <div className="px-3 pb-1.5 text-[10px] tracking-[0.15em] opacity-70 truncate" data-testid="upload-detail">
                    {uploadDetail}
                  </div>
                )}
                <div className="h-1 bg-zinc-200">
                  <div className="h-full bg-black" style={{ width: `${uploadPct}%` }} />
                </div>
              </div>
            )}
          </div>
          <HowItWorksCard />
        </section>

        {budget.tripped && (
          <section
            data-testid="budget-banner"
            className="border-2 border-black bg-[#FFE066] text-black px-4 py-3 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="flex items-start gap-2 flex-1 min-w-[260px]">
              <FileWarning className="w-5 h-5 mt-[2px] shrink-0" />
              <div className="leading-tight">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] font-bold">
                  LLM credit cap reached — extraction paused
                </div>
                <div className="font-mono text-[11px] tracking-[0.1em] opacity-80 mt-1">
                  No more files will run extraction until you top up. Already-processed reports are safe.
                  Top up at: <span className="font-bold">Profile → Universal Key → Add Balance</span> — or enable
                  <span className="font-bold"> auto top-up</span> there so this never happens mid-batch.
                </div>
              </div>
            </div>
            <button
              data-testid="budget-reset-btn"
              onClick={handleBudgetReset}
              className="inline-flex items-center gap-1 border-2 border-black bg-black text-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-800"
            >
              I&rsquo;ve topped up — resume
            </button>
          </section>
        )}

        <StatsSection jobId={jobId} stats={stats} pendingCount={pendingCount} budgetTripped={budget.tripped} />

        <UploadReconciliation
          lastUpload={lastUpload}
          stats={stats}
          pendingCount={pendingCount}
          onDismiss={() => setLastUpload(null)}
        />

        {isQueued && (
          <div
            data-testid="queue-banner"
            className="border-2 border-[#1d4ed8] bg-[#1d4ed8] text-white px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] flex items-center gap-2 flex-wrap"
          >
            <Clock className="w-4 h-4" />
            Another report is processing — your batch is
            <span data-testid="queue-position" className="font-bold">
              #{job?.queue_position || 1} in line
            </span>
            and will start automatically. Nothing to do — keep this page open or come back later.
          </div>
        )}

        {isProcessing && (
          <div
            data-testid="processing-banner"
            className="border-2 border-black bg-white px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] flex items-center justify-between gap-3 flex-wrap"
          >
            <span className="inline-flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Extracting {pendingCount} of {stats.total}…
            </span>
            <div className="flex items-center gap-3">
              <button
                data-testid="resume-stuck-btn"
                onClick={handleResumeStuck}
                className="inline-flex items-center gap-1 border border-black bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-black hover:text-white"
                title="If a file has been stuck pending for a while, click to re-queue it."
              >
                <RefreshCw className="w-3 h-3" /> Resume stuck
              </button>
              <span className="opacity-70">Auto-refresh every {POLL_INTERVAL / 1000}s</span>
            </div>
          </div>
        )}

        {stats.failed > 0 && (
          <div
            data-testid="failed-retry-banner"
            className="border-2 border-[#C1121F] bg-[#C1121F] text-white px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] flex items-center justify-between gap-3 flex-wrap"
          >
            <span className="inline-flex items-center gap-2">
              <FileWarning className="w-4 h-4" />
              {stats.failed} file{stats.failed === 1 ? "" : "s"} failed extraction
            </span>
            <button
              data-testid="retry-failed-btn"
              onClick={handleRetryFailed}
              disabled={retrying}
              className="inline-flex items-center gap-1 border-2 border-white bg-white text-[#C1121F] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] font-bold hover:bg-transparent hover:text-white disabled:opacity-60"
              title="Re-queue every failed file for another extraction attempt."
            >
              <RefreshCw className="w-3 h-3" /> Retry {stats.failed} failed
            </button>
          </div>
        )}

        <ReportFilters
          jobId={jobId}
          search={search}
          setSearch={setSearch}
          injuryFilter={injuryFilter}
          setInjuryFilter={setInjuryFilter}
          reportsCount={reports.length}
          qualifyingCount={stats.qualifying}
          failedCount={stats.failed}
          skippedCount={stats.skipped}
          onOpenFailed={() => setFailedOpen(true)}
          reEnrichCount={reEnrichCount}
          onReEnrich={handleReEnrich}
          reEnriching={reEnriching}
        />

        <section data-testid="reports-section">
          <ReportTable
            reports={reports}
            loading={loadingReports && reports.length === 0}
            onOpen={setOpenReport}
          />
        </section>

        <footer className="pt-10 pb-6 border-t border-black/10 text-center font-mono text-[10px] uppercase tracking-[0.3em] opacity-60">
          P12 codes — 01 Fatal · 02 Suspected Serious · 03 Suspected Minor
        </footer>
      </main>

      <ReportDetailModal report={openReport} onClose={() => setOpenReport(null)} />
      <FailedReportsModal
        open={failedOpen}
        onClose={() => setFailedOpen(false)}
        jobId={jobId}
        onRetried={() => {
          refreshJob();
          refreshReports();
        }}
      />

      {/* In-app confirm dialog (window.confirm is blocked by the preview iframe). */}
      {confirmClearOpen && (
        <div
          data-testid="clear-confirm-overlay"
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !clearing && setConfirmClearOpen(false)}
        >
          <div
            data-testid="clear-confirm-modal"
            className="bg-white border-2 border-black max-w-md w-full p-6 hard-shadow"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-chivo text-xl font-black tracking-tighter mb-2">
              Clear all uploaded reports?
            </h2>
            <p className="text-sm opacity-75 mb-5">
              This deletes the current job and every report in it. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                data-testid="clear-cancel-btn"
                onClick={() => setConfirmClearOpen(false)}
                disabled={clearing}
                className="border border-black bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="clear-confirm-btn"
                onClick={handleClear}
                disabled={clearing}
                className="border-2 border-red-600 bg-red-600 text-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-red-700 disabled:opacity-50"
              >
                {clearing ? "Clearing…" : "Yes, clear everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
